import { useContext, useEffect, useState } from 'react'
import { ModalContext } from '../ModalContext'
import { useSelector } from 'react-redux'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { UserIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { GetWorkoutTemplateNameFromKey } from 'functions/textUtils'
import { GetOrganizationPrimaryColorHex } from 'functions/styleUtils'
import MediumLoadingComponent from 'components/Loading/MediumLoadingComponent'

export default function IncompleteAthleteRecordsModal() {
    const { modalData } = useContext(ModalContext)
    const organizationId = useSelector((state) => state.user.organizationId)
    const userId = useSelector((state) => state.user.id)
    const brandColor = GetOrganizationPrimaryColorHex(organizationId)
    
    const toRgba = (hex, alpha = 1) => {
        const h = hex.replace('#','')
        const bigint = parseInt(h, 16)
        const r = (bigint >> 16) & 255
        const g = (bigint >> 8) & 255
        const b = bigint & 255
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }
    
    const [incompleteRecords, setIncompleteRecords] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        const fetchIncompleteWorkouts = async () => {
            if (!organizationId || !userId || !modalData?.workoutType || !modalData?.weekOfTheYearKeys) {
                setIsLoading(false)
                return
            }

            setIsLoading(true)
            setError(null)

            try {
                // Stringify array for GET request query params (backend expects JSON string or comma-separated)
                const weekKeysString = Array.isArray(modalData.weekOfTheYearKeys) 
                    ? JSON.stringify(modalData.weekOfTheYearKeys)
                    : modalData.weekOfTheYearKeys

                const jsonBody = {
                    organizationId: organizationId,
                    requestingUserId: userId,
                    workoutType: modalData.workoutType,
                    weekOfTheYearKeys: weekKeysString
                }

                const result = await WhichWeightAPI("REPORTING", "GET", "/organization_incomplete_workouts", jsonBody)

                if (result.data.status === "failure") {
                    setError(result.data.message || "Failed to fetch incomplete workouts")
                    setIncompleteRecords([])
                } else {
                    setIncompleteRecords(result.data.data || [])
                }
            } catch (err) {
                console.error('Error fetching incomplete workouts:', err)
                setError("An error occurred while fetching incomplete workouts")
                setIncompleteRecords([])
            } finally {
                setIsLoading(false)
            }
        }

        fetchIncompleteWorkouts()
    }, [organizationId, userId, modalData?.workoutType, modalData?.weekOfTheYearKeys])

    const formatWeekKey = (weekKey) => {
        if (!weekKey) return 'N/A'
        // Format: "2025-week-44" -> "Week 44, 2025"
        const parts = weekKey.split('-')
        if (parts.length >= 3) {
            const year = parts[0]
            const week = parts[2]
            return `Week ${week}, ${year}`
        }
        return weekKey
    }

    return (
        <>
            {/* Title */}
            <div className="text-center text-lg font-semibold leading-6 text-gray-900">
                {modalData?.titleText || 'Incomplete Workouts'}
            </div>

            {/* Content */}
            <div className="p-2">
                {isLoading ? (
                    <MediumLoadingComponent />
                ) : error ? (
                    <div className="text-center py-8 text-sm text-red-600">
                        {error}
                    </div>
                ) : incompleteRecords && incompleteRecords.length > 0 ? (
                    <div className="py-2 space-y-3 max-h-[75vh] overflow-y-auto">
                        {incompleteRecords.map((record, index) => (
                            <div 
                                key={`${record.userId}-${record.weekOfTheYearKey}-${record.sessionKey}-${index}`} 
                                className="flex items-start gap-3"
                            >
                                {/* User Icon and X Icon */}
                                <div className="flex-shrink-0 relative">
                                    <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                                        <UserIcon className="h-6 w-6 text-gray-500" />
                                    </div>
                                    <XCircleIcon className="absolute -top-1 -right-1 h-5 w-5 text-red-500 bg-white rounded-full" />
                                </div>
                                
                                {/* Name and Info */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-nowrap">
                                        <span className="text-sm font-medium text-gray-900 truncate">
                                            {[record.firstName, record.lastName].filter(Boolean).join(' ') || 'Unknown Athlete'}
                                        </span>
                                        {record.sessionKey && (
                                            <span 
                                                className="px-2 py-1 rounded-full text-xs font-medium flex-shrink-0"
                                                style={{
                                                    backgroundColor: toRgba(brandColor, 0.1),
                                                    color: brandColor
                                                }}
                                            >
                                                {GetWorkoutTemplateNameFromKey(record.sessionKey)}
                                            </span>
                                        )}
                                    </div>
                                    {record.weekOfTheYearKey && (
                                        <div className="text-xs text-gray-500 text-left">
                                            {formatWeekKey(record.weekOfTheYearKey)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8 text-sm text-gray-500">
                        No incomplete workouts found
                    </div>
                )}
            </div>
        </>
    )
}
